import React from 'react';
import PrintDocument, {
  DocSection, DocFieldGrid, DocField, DocNumber,
  formatDate, formatMoney, amountInArabicWords,
} from '../PrintDocument';
import { sectionLabel, personName, formatRegNo } from '../labels';
import './documents.css';

/** payment_method is stored as an enum ('cash'|'transfer'|'other') by
 *  the schema; a receipt must print the Arabic word, not the key. */
const METHOD_LABELS = {
  cash: 'نقداً',
  transfer: 'حوالة مصرفية',
  other: 'أخرى',
};

/**
 * PaymentReceiptDoc — وصل دفع قسط.
 *
 * The voucher handed over the counter the moment money changes hands.
 * Printed on A5, which is what a receipt is: half a sheet, usually two
 * copies (one for the payer, one for the file).
 *
 * WHY THE AMOUNT IS PRINTED TWICE
 * In digits AND written out in Arabic words, wrapped in "فقط … لا غير".
 * A digit is trivially altered after the fact — a 1 becomes a 7, a
 * zero is appended — and the closing "لا غير" stops anything being
 * added to the phrase either. This is the receipt's anti-forgery
 * mechanism and the single reason amountInArabicWords() exists in
 * PrintDocument.js. If the amount is beyond the helper's range it
 * returns '' and the words line is simply omitted rather than printing
 * a wrong phrase — a wrong written amount is worse than none.
 *
 * WHY THE BALANCE IS ON THE RECEIPT
 * The `payments` row stores fees_total_cents / total_paid_before_cents
 * / remaining_after_cents as a deliberate historical snapshot taken at
 * payment time (schema 0001, same pattern as exam_results). The
 * receipt prints THAT snapshot, never a freshly-computed balance: a
 * reprinted receipt must show what was true when it was issued, or it
 * contradicts the copy the parent is holding.
 *
 * MONEY UNITS: the columns are named *_cents but the app treats them
 * as whole dollars — formatMoney() matches Payments.js and
 * StudentDirectory.js exactly (toLocaleString, no division). Do not
 * divide by 100 here; that would silently disagree with every other
 * screen.
 *
 * PROPS CONTRACT
 *   payment   object   REQUIRED. The row returned by
 *                      `api.createPayment(...)` or one element of
 *                      `api.payments({ student_id })`
 *                      (route money.js createPayment / listPayments).
 *                      Fields read:
 *                        id, amount_cents, payment_type,
 *                        payment_method, note, fees_total_cents,
 *                        total_paid_before_cents, remaining_after_cents,
 *                        paid_at, student_name?, section_label?
 *   student   object?  The payer's pupil row (api.student(id) or a
 *                      studentDirectory row). Used for the name and
 *                      class when the payment row does not carry them
 *                      — createPayment's RETURNING * gives the raw
 *                      payments columns only, with no student_name.
 *                      Fields read: name, section_label, section_id,
 *                      grade_title, registration_number
 *   cashierName string? printed under the cashier's signature line
 *                      (pass session.user()?.name).
 *   logoUrl   string?  school logo; a placeholder box prints without it.
 *   docNumber string|number? overrides the receipt number, which
 *                      otherwise is the payment's own id.
 *   showToolbar / onClose / autoPrint — forwarded to PrintDocument.
 *
 * PURE / PRESENTATIONAL: fetches nothing, owns no state.
 */
export default function PaymentReceiptDoc({
  payment,
  student,
  cashierName,
  logoUrl,
  docNumber,
  showToolbar = true,
  onClose,
  autoPrint = false,
}) {
  if (!payment) return null;

  // An exemption is recorded as a payment row with amount_cents 0. It
  // is a real document (the parent needs proof the fee was waived) but
  // it is NOT a receipt for money, so it is titled and worded
  // differently rather than printing "استلمنا $0".
  const isExempt = payment.payment_type === 'أعفاء من القسط';

  const amount = Number(payment.amount_cents || 0);
  const words = isExempt ? '' : amountInArabicWords(amount);

  const payerName = student ? personName(student) : (payment.student_name || '—');
  const section = student && (student.section_label || student.section_id)
    ? sectionLabel(student)
    : (payment.section_label || null);

  const method = payment.payment_method
    ? (METHOD_LABELS[payment.payment_method] || payment.payment_method)
    : null;

  return (
    <PrintDocument
      size="a5"
      title={isExempt ? 'إشعار إعفاء من القسط' : 'وصل دفع قسط'}
      docNumber={docNumber ?? payment.id ?? undefined}
      issuedAt={payment.paid_at}
      logoUrl={logoUrl}
      // A receipt is a financial document, so it carries the
      // institution's REGISTERED name — the one that matches the
      // school's paperwork and stamp — rather than the product name the
      // rest of the system is branded with ("بوابة المتميزون").
      schoolName="مؤسسة المتميزون التعليمية"
      schoolSubtitle="الإدارة المالية"
      showToolbar={showToolbar}
      onClose={onClose}
      autoPrint={autoPrint}
      footerNote="يُرجى الاحتفاظ بهذا الوصل؛ فهو الإثبات الوحيد للدفع عند أي مراجعة."
      signatures={[
        { title: 'أمين الصندوق', name: cashierName || undefined },
        { title: 'توقيع المستلم' },
      ]}
    >
      <DocSection>
        <DocFieldGrid columns={1}>
          <DocField label="اسم الطالب" value={payerName} />
          <DocField label="الشعبة" value={section} />
          <DocField label="رقم التسجيل" value={formatRegNo(student?.registration_number)} numeric />
          <DocField label="التاريخ" value={formatDate(payment.paid_at)} numeric />
          <DocField label="نوع الدفعة" value={payment.payment_type} />
          {!isExempt && <DocField label="طريقة الدفع" value={method} />}
        </DocFieldGrid>
      </DocSection>

      {isExempt ? (
        <p className="ui-docx-amount-words">
          تُقرّ إدارة المدرسة بإعفاء الطالب المذكور أعلاه من قسط هذا العام،
          ولا يترتّب عليه أي مبلغ مستحق.
        </p>
      ) : (
        <>
          <div className="ui-docx-amount">
            <span className="ui-docx-amount-label">المبلغ المقبوض</span>
            <span className="ui-docx-amount-value ui-doc-num">{formatMoney(amount)}</span>
          </div>

          {/* The words line is omitted rather than left blank when the
              helper cannot express the number — see the header comment. */}
          {words && (
            <p className="ui-docx-amount-words">
              فقط <strong>{words}</strong> دولار أمريكي لا غير.
            </p>
          )}
        </>
      )}

      <DocSection title="الرصيد">
        <div className="ui-docx-balance">
          <span>المستحق حتى تاريخه</span>
          <DocNumber>{formatMoney(payment.fees_total_cents)}</DocNumber>
        </div>
        <div className="ui-docx-balance">
          <span>المدفوع سابقاً</span>
          <DocNumber>{formatMoney(payment.total_paid_before_cents)}</DocNumber>
        </div>
        <div className="ui-docx-balance ui-docx-balance-strong">
          <span>المتبقي بعد هذه الدفعة</span>
          <DocNumber>{formatMoney(payment.remaining_after_cents)}</DocNumber>
        </div>
      </DocSection>

      {payment.note && (
        <DocSection title="البيان">
          <p className="ui-docx-statement">{payment.note}</p>
        </DocSection>
      )}
    </PrintDocument>
  );
}
